/**
 * Email Logs Page
 * Delivery history for invoice emails, payment reminders and scheduled reports.
 *
 * Reads biz_email_log (via GET /api/email/log). This is a DIFFERENT table from
 * the "Notification Log" on System Status, which covers helpdesk/ticket
 * notifications only and never contains invoice emails.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { useAuth } from '@/features/auth/auth-context';
import { deliveryState, EMAIL_TYPE_LABELS, type EmailLogEntry } from './email-delivery';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Mail,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
} from 'lucide-react';

const TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'payment_reminder', label: 'Payment Reminder' },
  { value: 'overdue_notice', label: 'Overdue Notice' },
  { value: 'scheduled_report', label: 'Scheduled Report' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

function DeliveryBadge({ log }: { log: EmailLogEntry }) {
  switch (deliveryState(log)) {
    case 'confirmed':
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle2 className="h-3 w-3" /> Sent
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" /> Failed
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" /> Pending
        </Badge>
      );
    default:
      return (
        <Badge variant="warning" className="gap-1" title="Marked sent but no SendGrid message ID was recorded">
          <AlertTriangle className="h-3 w-3" /> Unconfirmed
        </Badge>
      );
  }
}

export function EmailLogsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [emailType, setEmailType] = useState('all');
  const [status, setStatus] = useState('all');
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<EmailLogEntry | null>(null);
  const limit = 25;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['email-log', emailType, status, scope, page],
    queryFn: async () => {
      const result = await api.getEmailLog({
        emailType: emailType !== 'all' ? emailType : undefined,
        status: status !== 'all' ? status : undefined,
        scope: isAdmin && scope === 'all' ? 'all' : undefined,
        page,
        limit,
      });
      return {
        logs: (result.data || []) as EmailLogEntry[],
        pagination: result.pagination || { page: 1, limit, total: 0, totalPages: 0 },
        appliedScope: result.scope || 'mine',
      };
    },
  });

  const logs = data?.logs || [];
  const pagination = data?.pagination || { page: 1, limit, total: 0, totalPages: 0 };
  const unconfirmed = logs.filter(l => deliveryState(l) === 'unconfirmed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Email Logs</h1>
        <p className="text-muted-foreground">
          Delivery history for invoice emails, payment reminders and scheduled reports
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-[200px] flex-1">
              <Select value={emailType} onValueChange={(v) => { setEmailType(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type..." />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_FILTERS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status..." />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="min-w-[180px]">
                <Select value={scope} onValueChange={(v) => { setScope(v as 'mine' | 'all'); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mine">My sends</SelectItem>
                    <SelectItem value="all">All users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => { setEmailType('all'); setStatus('all'); setScope('mine'); setPage(1); }}
            >
              Clear
            </Button>
          </div>
          {!isAdmin && (
            <p className="text-xs text-muted-foreground mt-3">
              Showing emails sent by you. Emails triggered by other users are recorded under their account.
            </p>
          )}
        </CardContent>
      </Card>

      {unconfirmed > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-900">
              {unconfirmed} {unconfirmed === 1 ? 'entry is' : 'entries are'} marked sent but carry no SendGrid
              message ID. These were logged while the SendGrid API key was unavailable and were most likely
              never actually delivered.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Log Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Sent Email History
            <span className="text-sm font-normal text-muted-foreground">
              ({pagination.total} records)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : isError ? (
            <div className="py-8 text-center space-y-2">
              <AlertTriangle className="h-8 w-8 mx-auto text-destructive opacity-70" />
              <p className="text-sm text-destructive font-medium">Could not load email logs</p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                A common cause is an out-of-date stored procedure. Run{' '}
                <code className="font-mono">npm run db:procedures</code> to redeploy{' '}
                <code className="font-mono">sp_fin_list_email_logs</code>, and check the API logs for
                the underlying error.
              </p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No email logs found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs">
                      <th className="text-left py-2 pr-3 font-medium">Sent</th>
                      <th className="text-left py-2 pr-3 font-medium">Type</th>
                      <th className="text-left py-2 pr-3 font-medium">Recipient</th>
                      <th className="text-left py-2 pr-3 font-medium">Subject</th>
                      <th className="text-left py-2 pr-3 font-medium">Status</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log.id}
                        className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                        onClick={() => setSelected(log)}
                      >
                        <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                          {formatDateTime(log.sentAt || log.createdAt)}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {EMAIL_TYPE_LABELS[log.emailType] || log.emailType}
                        </td>
                        <td className="py-2 pr-3 max-w-[220px] truncate" title={log.recipientEmail}>
                          {log.recipientName ? (
                            <>
                              <span className="font-medium">{log.recipientName}</span>{' '}
                              <span className="text-muted-foreground text-xs">{log.recipientEmail}</span>
                            </>
                          ) : (
                            log.recipientEmail
                          )}
                        </td>
                        <td className="py-2 pr-3 max-w-[280px] truncate" title={log.subject}>
                          {log.subject}
                        </td>
                        <td className="py-2 pr-3"><DeliveryBadge log={log} /></td>
                        <td className="py-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {selected && (EMAIL_TYPE_LABELS[selected.emailType] || selected.emailType)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="col-span-2">
                  <span className="text-muted-foreground">Subject:</span>{' '}
                  <span className="font-medium">{selected.subject}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Recipient:</span>{' '}
                  <span className="font-medium">
                    {selected.recipientName ? `${selected.recipientName} <${selected.recipientEmail}>` : selected.recipientEmail}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <DeliveryBadge log={selected} />
                </div>
                <div>
                  <span className="text-muted-foreground">Queued:</span>{' '}
                  <span className="font-medium">{formatDateTime(selected.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Sent:</span>{' '}
                  <span className="font-medium">
                    {selected.sentAt ? formatDateTime(selected.sentAt) : '-'}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">SendGrid Message ID:</span>{' '}
                  <span className="font-mono text-xs">{selected.sendgridMessageId || '-'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Entity:</span>{' '}
                  <span className="font-mono text-xs">{selected.entityType} / {selected.entityId}</span>
                </div>
              </div>

              {deliveryState(selected) === 'unconfirmed' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  This entry is marked <strong>sent</strong> but has no SendGrid message ID, which means the send
                  was skipped because no API key was configured at the time. Treat it as <strong>not delivered</strong>.
                </div>
              )}

              {selected.errorMessage && (
                <div>
                  <h4 className="font-medium mb-2 text-sm text-muted-foreground">Error</h4>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-48 whitespace-pre-wrap">
                    {selected.errorMessage}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
